import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { provideNgxMask } from 'ngx-mask';
import { Subscription } from 'rxjs';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';

@Component({
  selector: 'app-text-field',
  templateUrl: './text-field.component.html',
  styleUrl: './text-field.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => TextFieldComponent),
    },
    provideNgxMask({
      patterns: {
        K: { pattern: new RegExp("\[\u0400-\u052F\u2DE0-\u2DFF\uA640-\uA69F'a-zA-Z\]") },
        '0': { pattern: new RegExp('\[0-9\]') },
      },
    }),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TextFieldComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() formControl!: FormControl<string | number | null>;
  @Input() input: string | number = '';
  @Input() id: string = '';
  @Input() name: string = 'name';
  @Input() isPassword: boolean = false;
  @Input() defaultValue: string = '';
  @Input() label: string = '';
  @Input() customClass: string = '';
  @Input() placeholder: string = '';
  @Input() mask: string = '';
  @Input() prefix: string = '';
  @Input() suffix: string = '';
  @Input() maxLength: number | null = null;
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() type: 'date' | 'v2' | 'color' | 'default' = 'default';
  @Input() required: boolean = false;
  @Input() autofocus: boolean = false;
  @Input() error: boolean = false;
  @Input() inputType: 'text' | 'number' | 'password' | 'time' = 'text';
  @Input() min?: number | string | Date | null;
  @Input() max?: number | string | Date | null;

  @Input() isPlaceholderTranslate: boolean = true;

  @Output() onChangeClick = new EventEmitter();
  @Output() onChangeSave = new EventEmitter();
  @Output() onChangeCancel = new EventEmitter();
  @Output() onValueChange = new EventEmitter();

  @ViewChild('inputRef', { static: false }) inputRef!: ElementRef<HTMLInputElement>;

  public hide: boolean = true;
  private valueChangesSub?: Subscription;

  ngOnInit(): void {
    if (this.autofocus) {
      setTimeout((): void => {
        this.inputRef.nativeElement.focus();
      });
    }

    if (this.type === 'v2' && this.formControl) {
      this.valueChangesSub = this.formControl.valueChanges.subscribe((value) => {
        this.onValueChange.emit(value);
      });
    }
  }

  ngOnDestroy(): void {
    this.valueChangesSub?.unsubscribe();
  }

  onChange = (data: any): void => {};
  onTouch = (_: any): void => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  writeValue(value: string | number): void {
    if (this.type === 'v2') return;

    this.input = value;
    this.onChange(value);
  }

  /**
   *Func triggered on input change
   */
  onchange(event: any): void {
    let trimmedValue = event;
    const invalid = false;
    const isString = typeof trimmedValue === 'string';
    const isNumber = typeof trimmedValue === 'number';
    if (trimmedValue && isString) {
      trimmedValue = trimmedValue.trim();
    }

    if (invalid) {
      setTimeout(() => {
        this.writeValue(this.input);
      });
      return;
    }

    this.input = trimmedValue;
    this.onChange(trimmedValue);
    this.onValueChange.emit(trimmedValue);
  }

  protected onDateChange(event: MatDatepickerInputEvent<Date>): void {
    const date = event.value;
    if (date) {
      const isoDate = date.toISOString();
      this.formControl.setValue(isoDate);
    } else {
      this.formControl.setValue(null);
    }
  }

  protected clickKey(e: KeyboardEvent) {
    const functionalKeys = ['Backspace', 'ArrowRight', 'ArrowLeft'];
    if (this.inputType !== 'number' || typeof this.max !== 'number') return;

    if (functionalKeys.indexOf(e.key) !== -1) {
      return;
    }

    const keyValue = +e.key;
    if (isNaN(keyValue)) {
      e.preventDefault();
      return;
    }

    const newValue = this.inputRef.nativeElement.value + keyValue.toString();

    if (+newValue > this.max) {
      e.preventDefault();
      return;
    }
  }
}
